import React from 'react';
import UnexpectedErrorPage from './UnexpectedErrorPage';
import { ensureSentryInitialized } from '../../utils/initSentry';
import { PRIVATE_ROUTE } from '../../utils/constants';

type Props = {
  children: React.ReactNode;
  onBackToHome?: () => void;
  resetOnTryAgain?: 'state' | 'reload';
};

type State = {
  hasError: boolean;
};

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    void ensureSentryInitialized().then(() => {
      void import('@sentry/react').then((Sentry) => {
        Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
        });
      });
    });
  }

  handleTryAgain = () => {
    if (this.props.resetOnTryAgain === 'reload') {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return <UnexpectedErrorPage onTryAgain={this.handleTryAgain} onBackToHome={this.props.onBackToHome} />;
    }

    return this.props.children;
  }
}

export function withAppErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options?: Pick<Props, 'onBackToHome' | 'resetOnTryAgain'>,
) {
  const WrappedComponent = (props: P) => (
    <AppErrorBoundary {...options}>
      <Component {...props} />
    </AppErrorBoundary>
  );

  WrappedComponent.displayName = `withAppErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
}

export function createRouteBackToHome(history: { push: (path: string) => void }) {
  return () => history.push(PRIVATE_ROUTE.campaigns);
}

export default AppErrorBoundary;
